import { useState } from "react";
import type { FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Seo } from "@/components/seo/Seo";
import logo from "@/assets/logo.svg";
import { ArrowUpRight } from "lucide-react";

const engagementCards = [
  {
    title: "1. Your expertise runs as infrastructure.",
    body: "We encode your framework into a custom Expert Lens that teams can run on demand within the tools they already use. Your method stops living only in books and decks and starts living in their workflow."
  },
  {
    title: "2. Skills execute one expert move at a time.",
    body: "Inside the Lens, Skills turn your method into repeatable, high-value actions with clear inputs and a specific job to do, such as framing a decision, building a brief, or mapping trade-offs."
  },
  {
    title: "3. You unlock a scalable, recurring revenue stream.",
    body: "You earn 50% of the recurring usage revenue your Expert Lens generates, driven by active users who rely on your method. It becomes a warm, always-on channel for your premium services."
  }
];

const whyItems = [
  {
    title: "1. CO-BUILT AROUND YOUR EXPERTISE",
    body: "We map your proprietary method ( frameworks, decision rules, edge cases) into an Expert Lens that applies your standards reliably."
  },
  {
    title: "2. LIVES INSIDE CLIENT WORKFLOWS",
    body: "Your Lens doesn’t sit on a shelf. It produces briefs, plans, matrices, and playbooks inside the tools teams already use."
  },
  {
    title: "3. IP-SAFE AND FREE FOR YOU",
    body: "Your method is wrapped as a secure private implementation system. You don’t pay platform fees, and you earn recurring usage revenue."
  },
  {
    title: "4. A LONG-TERM GROWTH PARTNER",
    body: "As teams use your Lens, your analytics dashboard shows what’s working, where adoption is expanding, and what to productize next."
  }
];

const steps = [
  {
    num: "1",
    title: "Complete a short form about you and your book (framework).",
    desc: ""
  },
  {
    num: "2",
    title: "We review fit and then confirm next steps if it’s a match for the founding cohort.",
    desc: ""
  },
  {
    num: "3",
    title: "Together, we map your framework into a custom Expert Lens and launch it to your clients and readers.",
    desc: ""
  }
];

const builtWithYouSteps = [
  {
    num: "1",
    title: "Map the Lens",
    desc: "We capture your framework, standards, decision rules, and edge cases so the method itself is preserved as the expert system."
  },
  {
    num: "2",
    title: "Define the Skills",
    desc: "We break the method into repeatable expert moves with clear inputs, logic, and outputs: the tasks users need most often."
  },
  {
    num: "3",
    title: "Design the artifacts",
    desc: "Each Skill is shaped to produce a concrete artifact teams can edit, review, share, and use inside real workflows."
  },
  {
    num: "4",
    title: "Launch in workflow",
    desc: "Your Expert Lens goes live with usable Skills, so teams can run the right expert move exactly when the work calls for it."
  }
];

const bodyCopyClass =
  "space-y-4 text-[#111] [--tw-text-opacity:1] [&_p]:text-[17px] [&_p]:leading-[1.8] [&_p]:text-[#111] [&_p]:[--tw-text-opacity:1] [&_strong]:text-[#111] [&_li]:text-[16px] [&_li]:leading-[1.7] [&_li]:text-[#111] [&_li]:[--tw-text-opacity:1]";

const highlightParagraphClass = "text-[18px] leading-[1.6] text-[#000]";

const ctaButtonClass =
  "inline-flex items-center gap-2 rounded-full px-6 py-2 text-xs sm:text-sm bg-[linear-gradient(135deg,#f5a623,#ffd477)] text-[#111] font-semibold uppercase tracking-[0.12em] shadow-[0_16px_36px_rgba(0,0,0,0.35)] hover:brightness-110 transition-all";

const systemFontStack = "'Inter', -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Helvetica Neue', Arial, sans-serif";

const AuthorsLanding2 = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [bookTitle, setBookTitle] = useState("");
  const [methodType, setMethodType] = useState("");
  const [audience, setAudience] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const { toast } = useToast();

  const scrollToId = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setAlreadyRegistered(false);

    try {
      const { data, error } = await supabase.functions.invoke("submit-waitlist", {
        body: {
          name,
          email,
          userType: "expert",
          website,
          linkedin,
          bookTitle,
          methodType,
          audience
        }
      });

      if (error) throw error;

      setIsSubmitted(true);
      setAlreadyRegistered(Boolean(data?.alreadyRegistered));

      if (!data?.alreadyRegistered) {
        toast({
          title: "Success!",
          description: "You've been added to the Applyt experts list. We'll review your application and reach out.",
        });
      }
    } catch (err: unknown) {
      toast({
        title: "Error",
        description: (err instanceof Error ? err.message : "Request failed") || "Failed to submit. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black" style={{ fontFamily: systemFontStack }}>
      <Seo
        title="For Authors & Experts - Applyt Expert Lens"
        description="Turn your proven framework into a 24/7 Expert Lens: an AI implementation agent that runs your thinking inside client workflows and earns recurring revenue from monthly users."
      />
      <div className="sticky top-0 z-50 bg-[#050d1f] shadow-md">
        <div className="border-b border-white/10 bg-[#050d1f]/95">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col items-center">
            <button
              type="button"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="inline-flex flex-col items-center gap-1 focus:outline-none"
              aria-label="Applyt home"
            >
              <img src={logo} alt="Applyt" className="h-12 w-auto brightness-0 invert" />
              <div
                className="text-xs uppercase tracking-[0.1em] text-white/70"
                style={{ transform: "translate(-5mm, -3mm)" }}
              >
                Expert App for your goal
              </div>
            </button>
          </div>
        </div>

        <div className="border-t border-b border-white/10 bg-gradient-to-r from-[#0a1836] via-[#08142b] to-[#0a1836]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-col gap-3">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <nav className="flex flex-wrap items-center gap-6 text-[12px] uppercase tracking-[0.15em] text-white/80">
                <a href="#meaning" className="hover:text-white transition-colors">Expert Lens</a>
                <a href="#engagement" className="hover:text-white transition-colors">How It Works</a>
                <a href="#built-with-you" className="hover:text-white transition-colors">Built With You</a>
                <a href="#ip" className="hover:text-white transition-colors">IP & Security</a>
              </nav>
              <button
                onClick={() => scrollToId("apply")}
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#f5a623] to-[#ffd477] px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] text-[#111] shadow-[0_10px_30px_rgba(0,0,0,0.35)]"
              >
                Become an Applyt Expert
                <ArrowUpRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <main className="bg-white text-[#111] [--tw-text-opacity:1]">
        {/* Hero */}
        <section id="hero" className="scroll-mt-40 bg-gradient-to-br from-[#183469] via-[#102446] to-[#08142b] text-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 grid md:grid-cols-2 gap-10 items-center">
            <div className="space-y-6 text-center md:text-left">
              <div className="text-xs uppercase tracking-[0.2em] text-white/80">FOR AUTHORS, EXPERTS, COACHES & CONSULTANTS</div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold leading-tight">
                Your expertise is proven.
              </h1>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold leading-tight">
                <span className="text-amber-400">But until it lives inside the workflow, it cannot scale.</span>
              </h1>
              <p className="text-base md:text-lg text-white/90 max-w-xl mx-auto md:mx-0">
                Turn your method into a workflow infrastructure that teams can integrate into their workflows.
              </p>
              <br />
              <div className="flex flex-col sm:flex-row items-center justify-center md:justify-start gap-3 sm:gap-4">
                <Button onClick={() => scrollToId("apply")} className={`${ctaButtonClass} mt-6 sm:mt-0`}>
                  Become an Applyt Expert
                  <ArrowUpRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
              <p className="text-sm text-white/80">
                Joining as an expert is <span className="text-amber-300 font-semibold">completely free</span>.
                Then you earn recurring revenue as users subscribe to your method.
              </p>
            </div>

            <div className="flex justify-center md:justify-end">
              <div className="w-full max-w-none md:w-[720px] lg:w-[820px] xl:w-[920px] rounded-3xl border border-white/10 bg-gradient-to-br from-[#0b1022] via-[#111c33] to-[#05070f] shadow-2xl p-3">
                <div className="aspect-video w-full overflow-hidden rounded-2xl border border-white/15 bg-black">
                  <iframe
                    className="h-full w-full"
                    src="https://www.youtube.com/embed/pb-ZEXyQ6H0"
                    title="Applyt Expert Lens Demo"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Meaning */}
        <section id="meaning" className="scroll-mt-40 py-14 md:py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-gray-200 bg-white text-xs uppercase tracking-[0.12em] text-gray-600">
              <span className="w-2 h-2 rounded-full bg-amber-400" />
              Expert Lens
            </div>
            <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight uppercase">What scaling your EXPERTISE into an Expert Lens <br />means for you?</h1>
            <div className={bodyCopyClass}>
              <p>
                You’ve already proven your method works.<strong>The problem now is adoption at scale.</strong>.
              </p>
              <p>
                Most teams do not fail because they do not respect your methodology. They fail because your expertise still lives outside their day-to-day workflow, so they half-apply it, improvise, and drift back to old habits.
              </p>
              <p className="text-gray-900 font-semibold">So instead of hoping people will use your IP, you give them a way to:</p>
              <ul className="list-disc list-inside space-y-2">
                <li>
                  Turn your expertise into an always-on Lens that contains your method, standards, and decision logic.
                </li>
                <li>
                  Decompose that method into Skills: repeatable expert moves that users can run correctly inside their workflow.
                </li>
                <li>
                  Produce finished artifacts such as briefs, plans, matrices, and playbooks instead of generic chat outputs.
                </li>
                <li>
                  Add a recurring revenue stream from ongoing usage, with Applyt free for you as the expert.
                </li>
              </ul>

              <p className="text-gray-900 font-semibold">Your framework should not stop at insight. It should shape the work day to day.</p>

            </div>
            <br />
            <Button onClick={() => scrollToId("apply")} className={`${ctaButtonClass} mt-16`}>
              Become an Applyt Expert
              <ArrowUpRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </section>

        {/* Engagement */}
        <section id="engagement" className="scroll-mt-40 py-14 md:py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-5xl mx-auto space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-gray-200 bg-white text-xs uppercase tracking-[0.12em] text-gray-600">
              <span className="w-2 h-2 rounded-full bg-amber-400" />
              How it works
            </div>
            <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight uppercase">
              Once your Expert Lens is live, the method can be executed at scale.
              </h2>
            <div className="grid md:grid-cols-3 gap-4">
              {engagementCards.map((card) => (
                <Card key={card.title} className="border border-gray-200 shadow-sm bg-white– text-gray-900">
                  <CardContent className="p-5 space-y-3">
                    <h3 className="text-base font-semibold uppercase tracking-[0.08em]">{card.title}</h3>
                    <p className="text-sm text-gray-800 leading-7">{card.body}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            <p className="text-[17px] leading-[1.8] text-[#111]">
              For example, if <strong>five client teams</strong> each have <strong>10 active users</strong>, that’s <strong>50 people paying monthly</strong> to run your expertise. And you earn your share with <strong>zero extra calls</strong>.
            </p>
            <p className="text-[#222] text-[17px] leading-[1.8]"><strong>You’re no longer selling information. You’re scaling your intellectual system.</strong></p>
            <br />
            <Button onClick={() => scrollToId("apply")} className={`${ctaButtonClass} mt-16`}>
              Become an Applyt Expert
              <ArrowUpRight className="ml-2 h-4 w-4" />
            </Button>
            <br /><br /><br />
            <div className="mt-10 rounded-[36px] border-[2px] border-[#222] bg-[#dedede] px-6 lg:px-8 py-6 shadow-[0_18px_35px_rgba(0,0,0,0.12)]">
              <p className={highlightParagraphClass}>
                During your Applyt engagement, we turn your proven framework into a custom <strong>Expert Lens</strong>. 
                It guides users to produce finished plans, briefs, and strategies in your structure, with your name and method front and center.
              </p>
            </div>
          </div>
        </section>

        {/* Built With You */}
        <section id="built-with-you" className="scroll-mt-40 py-14 md:py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-gray-200 bg-white text-xs uppercase tracking-[0.12em] text-gray-600">
              <span className="w-2 h-2 rounded-full bg-amber-400" />
              Built With You
            </div>
            <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight">
              We work with you to transform your IP into a custom Expert Lens.
            </h2>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              {builtWithYouSteps.map((step, i) => (
                <div
                  key={step.num}
                  className={`grid grid-cols-[60px,1fr] items-start ${i < builtWithYouSteps.length - 1 ? "border-b border-gray-200" : ""}`}
                >
                  <div className="flex items-center justify-center py-6 text-lg font-bold text-[#111]">
                    {step.num}
                  </div>
                  <div className="py-6 pr-6 space-y-1">
                    <h3 className="text-base font-semibold text-[#111]">{step.title}</h3>
                    <p className="text-[15px] leading-[1.7] text-gray-700">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[17px] leading-[1.8] text-[#111]">
              The result is not just access to your ideas. It is access to your <strong>applied method</strong>.
            </p>
            <br />
            <Button onClick={() => scrollToId("apply")} className={`${ctaButtonClass} mt-16`}>
              Become an Applyt Expert
              <ArrowUpRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </section>

        {/* IP */}
        <section id="ip" className="scroll-mt-40 py-14 md:py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-gray-200 bg-white text-xs uppercase tracking-[0.12em] text-gray-600">
              <span className="w-2 h-2 rounded-full bg-amber-400" />
              IP &amp; Security
            </div>
            <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight">Your IP is safe by design.</h2>
            <div className={bodyCopyClass}>
              <p>You’ve spent years developing your method or framework set. So the obvious first question is: What happens to my IP if I put it into Applyt?</p>
              <p>
                Your IP stays yours. Applyt wraps your method as a private Expert Lens. It does not become public training data, and it does not become a content pool that can be resold.
              </p>
              <p>
                Your ideas stay under your name, inside a hardened professional environment, while your Expert Lens does the work.
              </p>
            </div>
            <div className="grid md:grid-cols-2 border border-gray-200 rounded-lg overflow-hidden">
              <div className="p-6 border-b md:border-b md:border-r border-gray-200">
                <h3 className="text-base font-semibold text-[#111] mb-3">What this means in practice</h3>
                <ul className="list-disc list-inside space-y-2 text-[15px] leading-[1.7] text-gray-700">
                  <li>Built on secure AWS infrastructure.</li>
                  <li>Source materials stored encrypted with strict access controls.</li>
                  <li>Your Lens is isolated and your standards are not shared.</li>
                </ul>
              </div>
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-base font-semibold text-[#111] mb-3">What we do not do</h3>
                <ul className="list-disc list-inside space-y-2 text-[15px] leading-[1.7] text-gray-700">
                  <li>We do not train public models on your IP.</li>
                  <li>We do not scrape your content.</li>
                  <li>We do not resell your method as a content pool.</li>
                </ul>
              </div>
              <div className="p-6 md:border-r border-gray-200">
                <h3 className="text-base font-semibold text-[#111] mb-3">What stays with you</h3>
                <ul className="list-disc list-inside space-y-2 text-[15px] leading-[1.7] text-gray-700">
                  <li>You keep ownership and attribution.</li>
                  <li>Outputs remain associated with your method.</li>
                  <li>You can update or remove materials at any time.</li>
                </ul>
              </div>
              <div className="p-6">
                <h3 className="text-base font-semibold text-[#111] mb-3">Why this matters</h3>
                <ul className="list-disc list-inside space-y-2 text-[15px] leading-[1.7] text-gray-700">
                  <li>Your ideas stay protected while your system becomes usable at scale.</li>
                  <li>Your name remains attached every time value is created.</li>
                  <li>Your legacy grows without losing control of your IP.</li>
                </ul>
              </div>
            </div>
            <br />
            <Button onClick={() => scrollToId("apply")} className={`${ctaButtonClass} mt-16`}>
              Become an Applyt Expert
              <ArrowUpRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </section>

        {/* Join */}
        <section id="join" className="scroll-mt-40 py-14 md:py-20 px-4 sm:px-6 lg:px-8 bg-[#f7f0e6]">
          <div className="max-w-5xl mx-auto space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-gray-200 bg-white text-xs uppercase tracking-[0.12em] text-gray-600">
              <span className="w-2 h-2 rounded-full bg-amber-400" />
              Founding Cohort
            </div>
            <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight">Join the Applyt Experts Community.</h2>
            <div className={bodyCopyClass}>
              <p>
                We are curating a small founding group of experts who want their expertise to run the work, not just inspire it.
              </p>
              <p>
                Early experts include <strong>David Siegel</strong>, former CEO of Meetup and author of Decide &amp; Conquer.
              </p>
              <p>
                For a leadership and decision-making Lens like that, example Skills could include Frame the Decision, Compare Strategic Options, and Draft a CEO Decision Brief. The artifacts produced could be a decision memo, options matrix, or leadership brief.
              </p>
              <p>
                This is not an open marketplace for anyone with an audience. We are building a serious group of experts whose methods deserve to become operational systems.
              </p>
            </div>
            <p className="text-[17px] leading-[1.8] text-[#111] font-semibold">Examples of Leadership &amp; Decision-Making Lens Applications:</p>
            <div className="grid md:grid-cols-2 border border-gray-200 rounded-lg overflow-hidden">
              <div className="p-6 md:border-r border-gray-200">
                <h3 className="text-base font-semibold text-[#111] mb-3">Example Skills</h3>
                <ul className="list-disc list-inside space-y-2 text-[15px] leading-[1.7] text-gray-700">
                  <li>Culture Resets</li>
                  <li>Strategic Options</li>
                  <li>CEO Decision-Making</li>
                </ul>
              </div>
              <div className="p-6">
                <h3 className="text-base font-semibold text-[#111] mb-3">Example Artifacts</h3>
                <ul className="list-disc list-inside space-y-2 text-[15px] leading-[1.7] text-gray-700">
                  <li>Founder legacy memo</li>
                  <li>Options matrix</li>
                  <li>Leadership brief</li>
                </ul>
              </div>
            </div>

            <br/>
            <div className="grid gap-4 md:grid-cols-2">
              <figure className="h-full flex items-center justify-center">
                <img
                  src="/images/david-lens-card.png"
                  alt="Example Applyt Expert Lens profile card"
                  className="max-h-80 w-auto object-contain rounded-[20px]"
                  loading="lazy"
                />
              </figure>
              <figure className="h-full flex items-center justify-center">
                <img
                  src="/images/david-lens-form.png"
                  alt="Example Applyt Expert Lens author intake form"
                  className="max-h-80 w-auto object-contain rounded-[20px]"
                  loading="lazy"
                />
              </figure>
            </div>
            <br/>
            <br />
            <Button onClick={() => scrollToId("apply")} className={`${ctaButtonClass} mt-16`}>
              Become an Applyt Expert
              <ArrowUpRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </section>

        {/* Fit */}
        <section className="scroll-mt-40 py-14 md:py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-gray-200 bg-white text-xs uppercase tracking-[0.12em] text-gray-600">
              <span className="w-2 h-2 rounded-full bg-amber-400" />
              Fit
            </div>
            <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight">Who Applyt is for, and who it is not for.</h2>
            <div className="grid md:grid-cols-2 border border-gray-200 rounded-lg overflow-hidden">
              <div className="p-6 md:border-r border-gray-200">
                <h3 className="text-base font-semibold text-[#111] mb-3">Applyt is for experts whose method deserves to become infrastructure.</h3>
                <ul className="list-disc list-inside space-y-2 text-[15px] leading-[1.7] text-gray-700">
                  <li>Authors with a proven framework.</li>
                  <li>Consultants with a repeatable methodology.</li>
                  <li>Coaches whose thinking needs better implementation.</li>
                  <li>Experts with an audience but limited operational adoption.</li>
                  <li>Thought leaders who want recurring revenue tied to ongoing usage.</li>
                </ul>
              </div>
              <div className="p-6">
                <h3 className="text-base font-semibold text-[#111] mb-3">This is not for experts who only want another content channel.</h3>
                <ul className="list-disc list-inside space-y-2 text-[15px] leading-[1.7] text-gray-700">
                  <li>Someone without a proven method yet.</li>
                  <li>Creators looking for a generic chatbot with their name on it.</li>
                  <li>Experts who want to reach without implementation.</li>
                  <li>Anyone unwilling to think in systems, standards, and workflow.</li>
                </ul>
              </div>
            </div>
            <br />
            <Button onClick={() => scrollToId("apply")} className={`${ctaButtonClass} mt-16`}>
              Become an Applyt Expert
              <ArrowUpRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </section>

        {/* Why */}
        <section className="scroll-mt-40 py-14 md:py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-5xl mx-auto space-y-6">
            <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight uppercase">Why Applyt is different</h2>
            <p className="text-[17px] leading-[1.8] text-[#111]">
              Applyt isn’t a course platform, a content library, or yet another generic AI tool you’ll never check again. It’s built specifically for experts who want their expertise to run inside a real workflow, and earn from ongoing usage, not one-off launches.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {whyItems.map((item) => (
                <Card key={item.title} className="border border-gray-200 shadow-sm bg-white text-gray-900">
                  <CardContent className="p-5 space-y-2">
                    <h3 className="text-sm font-semibold uppercase tracking-[0.08em]">{item.title}</h3>
                    <p className="text-[16px] leading-[1.7] text-[#111]">{item.body}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            <p className="italic text-[#222] text-[17px] leading-[1.8]">
              If you want your expertise to outlive launches and become the way your clients work - driving measurable impact, ongoing value, and expansion - joining the Applyt Experts Community is the next step.
            </p>
            <br />
            <Button onClick={() => scrollToId("apply")} className={`${ctaButtonClass} mt-16`}>
              Become an Applyt Expert
              <ArrowUpRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </section>

        {/* Apply */}
        <section id="apply" className="scroll-mt-40 py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#183469] via-[#102446] to-[#08142b] text-white">
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8 items-start">
            <div className="space-y-4">
              <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight uppercase">
                Do not let your best ideas stop at inspiration.
              </h2>
              <p className="text-base text-white/85 leading-7">
                Fill out this short form to tell us about your framework and audience. Our team will review fit and reach out with next steps for the founding experts cohort.
              </p>
              <p className="text-base text-white/85 leading-7">
                This form is an expression of interest. It does not obligate you to anything. We will review fit and follow up by email.
              </p>
            </div>

            <Card className="bg-white/5 border border-white/15 shadow-2xl">
              <CardContent className="p-5">
                {!isSubmitted ? (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="name" className="text-sm text-white/90">
                        Full name<span className="text-amber-300 ml-1">*</span>
                      </Label>
                      <Input
                        id="name"
                        type="text"
                        placeholder="Your name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        disabled={isLoading}
                        className="bg-white/5 border-white/20 text-white placeholder:text-white/60"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="email" className="text-sm text-white/90">
                        Email<span className="text-amber-300 ml-1">*</span>
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled={isLoading}
                        className="bg-white/5 border-white/20 text-white placeholder:text-white/60"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label htmlFor="website" className="text-sm text-white/90">Website</Label>
                        <Input
                          id="website"
                          type="url"
                          placeholder="https://yoursite.com"
                          value={website}
                          onChange={(e) => setWebsite(e.target.value)}
                          disabled={isLoading}
                          className="bg-white/5 border-white/20 text-white placeholder:text-white/60"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="linkedin" className="text-sm text-white/90">
                          LinkedIn<span className="text-amber-300 ml-1">*</span>
                        </Label>
                        <Input
                          id="linkedin"
                          type="url"
                          placeholder="https://linkedin.com/in/..."
                          value={linkedin}
                          onChange={(e) => setLinkedin(e.target.value)}
                          required
                          disabled={isLoading}
                          className="bg-white/5 border-white/20 text-white placeholder:text-white/60"
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="bookTitle" className="text-sm text-white/90">
                        Book / method title<span className="text-amber-300 ml-1">*</span>
                      </Label>
                      <Input
                        id="bookTitle"
                        type="text"
                        placeholder="Title of your book, flagship program, or framework"
                        value={bookTitle}
                        onChange={(e) => setBookTitle(e.target.value)}
                        required
                        disabled={isLoading}
                        className="bg-white/5 border-white/20 text-white placeholder:text-white/60"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="audience" className="text-sm text-white/90">
                        Your current audience
                      </Label>
                      <Textarea
                        id="audience"
                        placeholder="Who uses or follows your method today? (e.g. tech teams, founders, managers, etc.)"
                        value={audience}
                        onChange={(e) => setAudience(e.target.value)}
                        disabled={isLoading}
                        rows={3}
                        className="bg-white/5 border-white/20 text-white placeholder:text-white/60 resize-none"
                      />
                    </div>

                    <br />
                    <Button
                      type="submit"
                      disabled={isLoading}
                      className={`${ctaButtonClass} mt-16 w-full justify-center`}
                    >
                      {isLoading ? "Submitting..." : "Become an Applyt Expert"}
                    </Button>
                  </form>
                ) : (
                  <div className="py-8 space-y-4 text-white">
                    <div className="space-y-2">
                      <h3 className="text-xl font-semibold">Thanks for your application!</h3>
                      <p className="text-white/80">We'll be in touch soon.</p>
                    </div>
                    {alreadyRegistered && (
                      <div className="px-3 py-2 rounded-md border border-white/20 bg-white/5 text-sm text-white/90">
                        Already registered
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-6 border-t border-border text-xs text-muted-foreground flex items-center justify-between bg-background px-4 sm:px-6 lg:px-8">
          <span>© {new Date().getFullYear()} Applyt. All rights reserved.</span>
          <span className="px-3 py-1 rounded-full border border-border">Authors cohort: applications open</span>
        </footer>
      </main>
    </div>
  );
};

export default AuthorsLanding2;
